/**
 * Step 2 — Suggested Ideas input.
 *
 * Calls `agentLabApi.invokeBriefIdeas({ client_uid, persona_id })` and
 * renders ranked `BriefIdea` cards.  Selecting a card seeds the draft
 * preview and advances to commit.
 */

import { useEffect, useState } from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import { agentLabApi, type BriefIdea, type BriefIdeasResponse } from '@/services/api';

interface Props {
  clientUid: string;
  personaId: string;
  onSelect: (idea: BriefIdea) => void;
}

export function SuggestedIdeasInput({ clientUid, personaId, onSelect }: Props) {
  const [ideas, setIdeas] = useState<BriefIdea[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    agentLabApi
      .invokeBriefIdeas({ client_uid: clientUid, persona_id: personaId })
      .then((res: BriefIdeasResponse) => {
        if (cancelled) return;
        setIdeas(res.ideas ?? []);
      })
      .catch((err) => {
        if (!cancelled) setError(String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientUid, personaId]);

  return (
    <div className="space-y-3">
      <div className="text-[11px] text-slate-500">
        Ranked by PDO concept activation strength for{' '}
        <span className="font-mono text-slate-700">{personaId}</span>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-[12px] text-slate-500 py-8 justify-center">
          <Loader2 size={14} className="animate-spin" />
          Loading PDO-grounded ideas…
        </div>
      )}

      {error && (
        <div className="text-[12px] text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-3">
          Failed to load ideas: {error}
        </div>
      )}

      {!loading && !error && ideas.length === 0 && (
        <div className="text-[12px] text-slate-500 py-8 text-center">
          No ranked ideas available for this persona.
        </div>
      )}

      <div className="space-y-2">
        {ideas.map((idea) => (
          <BriefIdeaCard key={idea.idea_id} idea={idea} onClick={() => onSelect(idea)} />
        ))}
      </div>
    </div>
  );
}

function BriefIdeaCard({ idea, onClick }: { idea: BriefIdea; onClick: () => void }) {
  const resonance = idea.predicted_resonance ?? 0;
  const tone =
    resonance >= 0.75
      ? 'text-teal-700'
      : resonance >= 0.5
        ? 'text-amber-700'
        : 'text-slate-500';
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left rounded-xl border border-slate-200 bg-white px-4 py-3 hover:border-teal-400 hover:shadow-sm transition group"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Sparkles size={11} className="text-teal-500" />
            <h4 className="text-sm font-semibold text-slate-900 truncate">
              {idea.title}
            </h4>
          </div>
          <p className="text-[12px] text-slate-600 mt-1 leading-snug line-clamp-2">
            {idea.premise}
          </p>
          {idea.driving_factors && idea.driving_factors.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {idea.driving_factors.slice(0, 4).map((f, i) => (
                <span
                  key={i}
                  className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-600"
                  title={f.edge_type}
                >
                  {(f.concept || '').replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className={`text-[10px] font-bold ${tone} whitespace-nowrap`}>
          {Math.round(resonance * 100)}%
        </div>
      </div>
    </button>
  );
}
